import { useEffect, useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  getParentLockStatus,
  setParentPin,
  verifyParentPin,
} from "@/lib/parent-lock.functions";

const SESSION_KEY = "parent_lock_unlocked";

type Props = {
  userId: string | null;
  toolName: string; // e.g. "Generator", "Essay Reviewer"
  children: ReactNode;
};

export function ParentLockGate({ userId, toolName, children }: Props) {
  const getStatus = useServerFn(getParentLockStatus);
  const verify = useServerFn(verifyParentPin);
  const setPin = useServerFn(setParentPin);

  const [loading, setLoading] = useState(true);
  const [hasPin, setHasPin] = useState(false);
  const [accountType, setAccountType] = useState<"adult" | "student">("adult");
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getStatus();
        if (cancelled) return;
        setHasPin(status.hasPin);
        setAccountType(status.accountType);
        if (typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1") {
          setUnlocked(true);
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not load access lock status");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, getStatus]);

  if (!userId || loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Adults skip the lock entirely.
  if (accountType !== "student") return <>{children}</>;

  if (unlocked) return <>{children}</>;

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await verify({ data: { pin } });
      if (!res.ok) {
        toast.error("Incorrect PIN.");
        return;
      }
      sessionStorage.setItem(SESSION_KEY, "1");
      setUnlocked(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin !== pin2) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await setPin({ data: { newPin: pin } });
      sessionStorage.setItem(SESSION_KEY, "1");
      setHasPin(true);
      setUnlocked(true);
      toast.success("Access PIN set. The Generator and Reviewer are now protected.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not set PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasPin ? <Lock className="h-5 w-5 text-primary" /> : <ShieldCheck className="h-5 w-5 text-primary" />}
            {hasPin ? "Access PIN required" : "Set an access PIN"}
          </CardTitle>
          <CardDescription>
            {hasPin ? (
              <>The {toolName} is locked. An access PIN is required to unlock it for this session.</>
            ) : (
              <>
                Before students can use the {toolName}, set an access PIN.
                The Tutor (Build Your Own) stays open without a PIN so students can keep practising.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasPin ? (
            <form onSubmit={handleUnlock} className="space-y-3">
              <div>
                <Label htmlFor="pin">Access PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPin1(e.target.value.replace(/\D/g, ""))}
                  placeholder="4–8 digits"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || pin.length < 4}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Unlock {toolName}
              </Button>
              <p className="text-xs text-muted-foreground">
                Forgot the PIN? Reset it from Account → Access lock.
              </p>
            </form>
          ) : (
            <form onSubmit={handleSetup} className="space-y-3">
              <div>
                <Label htmlFor="newpin">New access PIN</Label>
                <Input
                  id="newpin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPin1(e.target.value.replace(/\D/g, ""))}
                  placeholder="4–8 digits"
                  required
                />
              </div>
              <div>
                <Label htmlFor="newpin2">Confirm PIN</Label>
                <Input
                  id="newpin2"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin2}
                  onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || pin.length < 4}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Set access PIN
              </Button>
              <p className="text-xs text-muted-foreground">
                The PIN is stored as a one-way hash — even we can't read it.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
